/* eslint-disable no-console */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Switch,
  Input,
  Skeleton,
  HStack,
} from '@chakra-ui/react';

import { useSelector } from 'react-redux';
import TextEditor from '~/components/TextEditor';
import { ApplicationState } from '~/store/store';
import { colors } from '~/styles/colors';
import { toastError, toastSuccess } from '~/helpers/toast';
import ProposalService from '~/store/ducks/Proposal/services';
import {
  EquipmentVariables,
  ProposalCommercialConditionsEquipment,
  ProposalSectionProps,
} from '~/store/ducks/Proposal/types';
import Warning from '~/components/Form/Warning';
import { Button } from '~/components/Form/Button/Button_Styles';

const CommercialCondition: React.FC<ProposalSectionProps> = ({ isExpanded }) => {
  const { id: projectId } = useSelector((state: ApplicationState) => state.project);
  const { data: proposalData, equipments: commercialConditionsEquipments } = useSelector(
    (state: ApplicationState) => state.proposal
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [commercialConditions, setCommercialConditions] = useState(null);
  const [options, setOptions] = useState(null);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isTextModified, setIsTextModified] = useState<boolean>(false);

  const [prices, setPrices] = useState([]);

  const [pricingCompositionValue, setPricingCompositionValue] = useState('');
  const [pricingCompositionText, setPricingCompositionText] = useState('');

  const [transportationValue, setTransportationValue] = useState('');
  const [transportationText, setTransportationText] = useState('');

  const [deliveryTimeValue, setDeliveryTimeValue] = useState('');
  const [deliveryTimeText, setDeliveryTimeText] = useState('');

  const [paymentConditionValue, setPaymentConditionValue] = useState('');
  const [paymentConditionText, setPaymentConditionText] = useState('');

  const [warrantyValue, setWarrantyValue] = useState('');
  const [warrantyText, setWarrantyText] = useState('');

  const [installationValue, setInstallationValue] = useState('');
  const [installationText, setInstallationText] = useState('');

  const [creditRegistrationValue, setCreditRegistrationValue] = useState('');
  const [creditRegistrationText, setCreditRegistrationText] = useState('');

  const [proposalValidityValue, setProposalValidityValue] = useState('');
  const [proposalValidityText, setProposalValidityText] = useState('');

  const [priceEnabled, setPriceEnabled] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  const firstRender = useRef(true);

  const sumEquipmentPrices = (response, updatedPrices?): void => {
    if (updatedPrices) {
      setTotalPrice(
        updatedPrices
          .reduce((acc, price) => acc + price.quantity * price.unitPrice, 0)
          .toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
      );
      return;
    }

    setTotalPrice(
      response?.prices
        .reduce((acc, price) => acc + price.quantity * price.unitPrice, 0)
        .toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
    );
  };

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const response = await ProposalService.getCommercialConditionsOptions();
      const parsedOptions = ProposalService.parseCommercialConditionsOptions(response.data);
      setOptions(parsedOptions);
    } catch (err) {
      console.error(err);
      toastError('proposal', 'Erro ao recuperar condiçoes comerciais.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommercialConditions = async () => {
    try {
      setLoading(true);
      const response = await ProposalService.getCommercialConditions(projectId, proposalData.id);
      setCommercialConditions(response.data);
      sumEquipmentPrices(response.data);
    } catch (err) {
      console.error(err);
      toastError('proposal', 'Erro ao recuperar condições comerciais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      fetchOptions();
      fetchCommercialConditions();
    }

    return () => setCommercialConditions(null);
  }, [isExpanded]);

  useEffect(() => {
    if (commercialConditions) {
      // Load Prices
      setPrices(commercialConditions?.prices);

      // Load PricingComposition
      setPricingCompositionValue(commercialConditions?.pricingComposition.template.id);
      setPricingCompositionText(commercialConditions?.pricingComposition.text);

      // Load PaymentConditions
      setPaymentConditionValue(commercialConditions?.paymentConditions.template.id);
      setPaymentConditionText(commercialConditions?.paymentConditions.text);

      // Load Warranty
      setWarrantyValue(commercialConditions?.warranty.template.id);
      setWarrantyText(commercialConditions?.warranty.text);

      // Load Installation
      setInstallationValue(commercialConditions?.installation.template.id);
      setInstallationText(commercialConditions?.installation.text);

      // Load DeliveryTime
      setDeliveryTimeValue(commercialConditions?.deliveryTime.template.id);
      setDeliveryTimeText(commercialConditions?.deliveryTime.text);

      // Load Transportation
      setTransportationValue(commercialConditions?.transportation.template.id);
      setTransportationText(commercialConditions?.transportation.text);

      // Load Credit Registration
      setCreditRegistrationValue(commercialConditions?.creditRegistration.template.id);
      setCreditRegistrationText(commercialConditions?.creditRegistration.text);

      // Load Proposal Validity
      setProposalValidityValue(commercialConditions?.proposalValidity.template.id);
      setProposalValidityText(commercialConditions?.proposalValidity.text);
    }
  }, [commercialConditions]);

  useEffect(() => {
    if (commercialConditions) {
      if (firstRender.current) {
        firstRender.current = false;
      } else {
        setIsTextModified(true);
      }
    }
  }, [
    prices,
    pricingCompositionText,
    transportationValue,
    transportationText,
    deliveryTimeValue,
    deliveryTimeText,
    paymentConditionValue,
    paymentConditionText,
    warrantyText,
    installationValue,
    installationText,
    creditRegistrationText,
    proposalValidityText,
  ]);

  const handleChangePaymentCondition = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setPaymentConditionValue(value);
    const condition = options.paymentConditions.find((s: any) => s.id === +value);
    setPaymentConditionText(condition?.text || '');
  };

  const handleChangeDeliveryTime = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setDeliveryTimeValue(value);
    const deliveryTme = options.deliveryTime.find((s: any) => s.id === +value);
    setDeliveryTimeText(deliveryTme?.text || '');
  };

  const handleChangeTransportationValue = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setTransportationValue(value);
    const shipping = options.transportation.find((s: any) => s.id === +value);
    setTransportationText(shipping?.text || '');
  };

  const handleChangeInstallationValue = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setInstallationValue(value);
    const installation = options.installation.find((s: any) => s.id === +value);
    setInstallationText(installation?.text || '');
  };

  const handleChangeShowPrice = () => {
    setPriceEnabled(!priceEnabled);
  };

  // SAVE TEXTS
  const saveChangePricesVariable = (event: ChangeEvent<HTMLInputElement>, price, type: string) => {
    const { value } = event.target;
    const newValue = value !== '' ? parseFloat(value) : null;

    const newPrices = prices.map((pric) => {
      if (pric.id === price.id) {
        switch (type) {
          case EquipmentVariables.ICMS:
            return { ...pric, icms: newValue };
          case EquipmentVariables.PISCOFINS:
            return { ...pric, pisCofins: newValue };
          case EquipmentVariables.IPI:
            return { ...pric, ipi: newValue };
          case EquipmentVariables.PRICE:
            return { ...pric, unitPrice: newValue };
          default:
            break;
        }
      }
      return pric;
    });
    setPrices(newPrices);
    sumEquipmentPrices(null, newPrices);
  };

  const savePaymentConditionText = (markdown: string) => {
    setPaymentConditionText(markdown);
  };

  const saveWarrantyText = (markdown: string) => {
    setWarrantyText(markdown);
  };

  const saveInstallationText = (markdown: string) => {
    setInstallationText(markdown);
  };

  const saveDeliveryTimeText = (markdown: string) => {
    setDeliveryTimeText(markdown);
  };

  const saveTransportationText = (markdown: string) => {
    setTransportationText(markdown);
  };

  const saveCreditRegistrationText = (markdown: string) => {
    setCreditRegistrationText(markdown);
  };

  const saveProposalValidityText = (markdown: string) => {
    setProposalValidityText(markdown);
  };

  const savePricingCompositionText = (markdown: string) => {
    setPricingCompositionText(markdown);
  };

  const handleSubmit = async () => {
    const {
      pricingComposition,
      paymentConditions,
      warranty,
      installation,
      deliveryTime,
      transportation,
      creditRegistration,
      proposalValidity,
    } = commercialConditions;

    console.log('commercialConditions => ', commercialConditions);

    const newPricingComposition = {
      ...pricingComposition,
      template: {
        id: +pricingCompositionValue,
      },
      text: pricingCompositionText,
    };

    const newPaymentConditions = {
      ...paymentConditions,
      template: {
        id: +paymentConditionValue,
      },
      text: paymentConditionText,
    };

    const newWarranty = {
      ...warranty,
      template: {
        id: +warrantyValue,
      },
      text: warrantyText,
    };

    const newInstallation = {
      ...installation,
      template: {
        id: +installationValue,
      },
      text: installationText,
    };

    const newDeliveryTime = {
      ...deliveryTime,
      template: {
        id: +deliveryTimeValue,
      },
      text: deliveryTimeText,
    };

    const newTransportation = {
      ...transportation,
      template: {
        id: +transportationValue,
      },
      text: transportationText,
    };

    const newCreditRegistration = {
      ...creditRegistration,
      template: {
        id: +creditRegistrationValue,
      },
      text: creditRegistrationText,
    };

    const newProposalValidity = {
      ...proposalValidity,
      template: {
        id: +proposalValidityValue,
      },
      text: proposalValidityText,
    };

    const body = {
      ...commercialConditions,
      prices,
      pricingComposition: newPricingComposition,
      paymentConditions: newPaymentConditions,
      warranty: newWarranty,
      installation: newInstallation,
      deliveryTime: newDeliveryTime,
      transportation: newTransportation,
      creditRegistration: newCreditRegistration,
      proposalValidity: newProposalValidity,
    };

    try {
      setButtonLoading(true);
      await ProposalService.saveCommercialConditions(projectId, proposalData.id, body);
      toastSuccess('proposal', 'Salvo com sucesso!');
      setIsTextModified(false);
    } catch (err) {
      console.error(err);
      toastError('proposal', 'Erro ao salvar Seção.');
    } finally {
      setButtonLoading(false);
    }
  };

  const findEquipmentNameBySkuId = (equipment) => {
    try {
      return commercialConditionsEquipments.find(
        (equip: ProposalCommercialConditionsEquipment) => equip.id === equipment?.productSku?.id
      ).sku;
    } catch (err) {
      return '';
    }
  };

  return (
    <Skeleton isLoaded={!loading}>
      {options && (
        <Box className="proposal__section">
          <Text className="proposal__title"> Equipamentos x Quantidade </Text>
          <FormControl display="flex" alignItems="center" mt={5}>
            <FormLabel htmlFor="show-price" mb="0" fontSize={14} color={colors.gray200}>
              Exibir preço por equipamento?
            </FormLabel>
            <Switch size="sm" id="show-price" onChange={handleChangeShowPrice} isChecked={priceEnabled} />
          </FormControl>
          <Box overflowX="auto" my={5}>
            <Table size="sm" variant="striped" colorScheme="blackAlpha">
              <Thead>
                <Tr>
                  <Th>Item</Th>
                  <Th />
                  <Th colSpan={priceEnabled ? 4 : 3}>Impostos e Taxas Inclusos</Th>
                </Tr>
                <Tr>
                  <Th>Modelo</Th>
                  <Th>Quantidade</Th>
                  <Th>ICMS</Th>
                  <Th>PIS/CONFIS</Th>
                  <Th>IPI</Th>
                  {priceEnabled && <Th>Preço</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {prices?.map((price: any) => (
                  <Tr key={price.skuId}>
                    <Td>{findEquipmentNameBySkuId(price)}</Td>
                    <Td>{price?.quantity}</Td>
                    <Td>
                      <Input
                        placeholder="ICMS"
                        size="xs"
                        borderColor={colors.gray50}
                        type="number"
                        value={price?.icms}
                        onChange={(e) => saveChangePricesVariable(e, price, EquipmentVariables.ICMS)}
                      />
                    </Td>
                    <Td>
                      <Input
                        placeholder="PIS/COFINS"
                        size="xs"
                        borderColor={colors.gray50}
                        type="number"
                        value={price?.pisCofins}
                        onChange={(e) => saveChangePricesVariable(e, price, EquipmentVariables.PISCOFINS)}
                      />
                    </Td>
                    <Td>
                      <Input
                        placeholder="IPI"
                        size="xs"
                        borderColor={colors.gray50}
                        type="number"
                        value={price?.ipi}
                        onChange={(e) => saveChangePricesVariable(e, price, EquipmentVariables.IPI)}
                      />
                    </Td>
                    {priceEnabled && (
                      <Td>
                        <Input
                          placeholder="Preço"
                          size="xs"
                          borderColor={colors.gray50}
                          type="number"
                          value={price?.unitPrice}
                          onChange={(e) => saveChangePricesVariable(e, price, EquipmentVariables.PRICE)}
                        />
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
              <TableCaption placement="bottom" textAlign="left" mt={1} paddingLeft={1}>
                <Text>Preço Total com Impostos e Taxas</Text>
                <Text fontSize={16} color={colors.gray200}>
                  {totalPrice}
                </Text>
              </TableCaption>
            </Table>
          </Box>

          <Box className="proposal__subsection">
            <Text className="proposal__title"> Os valores estão compostos da seguinte forma </Text>
            <TextEditor markdown={pricingCompositionText} saveContentEditor={savePricingCompositionText} />

            <Text className="proposal__subtitle"> Condições de Pagamento</Text>
            <Select
              size="xs"
              backgroundColor="white"
              width={250}
              placeholder="Selecione"
              onChange={(e) => handleChangePaymentCondition(e)}
              value={paymentConditionValue}
            >
              {options.paymentConditions.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </Select>
            {paymentConditionValue && (
              <TextEditor markdown={paymentConditionText} saveContentEditor={savePaymentConditionText} />
            )}
            {/* <TextEditor markdown="commercialConditions?.priceInfo.text" saveContentEditor={submitContentEditor} /> */}
            <Text className="proposal__subtitle"> Garantia</Text>
            <TextEditor markdown={warrantyText} saveContentEditor={saveWarrantyText} />
          </Box>

          <Box className="proposal__subsection">
            <Text className="proposal__title"> Importante </Text>
            <Select
              size="xs"
              backgroundColor="white"
              width={250}
              placeholder="Selecione"
              onChange={(e) => handleChangeInstallationValue(e)}
              value={installationValue}
            >
              {options.installation.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </Select>
            {installationValue && <TextEditor markdown={installationText} saveContentEditor={saveInstallationText} />}
          </Box>

          <Box className="proposal__subsection">
            <Text className="proposal__title"> Prazo de Entrega </Text>
            <Select
              size="xs"
              backgroundColor="white"
              width={250}
              placeholder="Selecione"
              onChange={(e) => handleChangeDeliveryTime(e)}
              value={deliveryTimeValue}
            >
              {options.deliveryTime.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </Select>
            {deliveryTimeValue && <TextEditor markdown={deliveryTimeText} saveContentEditor={saveDeliveryTimeText} />}
          </Box>

          <Box className="proposal__subsection">
            <Text className="proposal__title"> Transporte </Text>
            <Select
              size="xs"
              backgroundColor="white"
              width={250}
              placeholder="Selecione"
              onChange={(e) => handleChangeTransportationValue(e)}
              value={transportationValue}
            >
              {options.transportation.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </Select>
            {transportationValue && (
              <TextEditor markdown={transportationText} saveContentEditor={saveTransportationText} />
            )}
          </Box>

          <Box className="proposal__subsection">
            <Text className="proposal__title"> Cadastro/Abertura de Crédito </Text>
            <TextEditor markdown={creditRegistrationText} saveContentEditor={saveCreditRegistrationText} />
          </Box>

          <Box className="proposal__subsection">
            <Text className="proposal__title"> Validade da Proposta </Text>
            <TextEditor markdown={proposalValidityText} saveContentEditor={saveProposalValidityText} />
          </Box>
        </Box>
      )}
      {isTextModified && <Warning message="As alterações ainda não foram salvas." />}
      <HStack mt={5} justify="end">
        <Button isLoading={buttonLoading} disabled={!isTextModified} $primary onClick={handleSubmit}>
          Salvar
        </Button>
      </HStack>
    </Skeleton>
  );
};

export default CommercialCondition;
